/** sessionStorage: show registration email confirmation modal after Auth0 redirect error. */
export const EMAIL_VERIFICATION_REQUIRED_STORAGE_KEY = 'club4x4_email_verification_required'

const EMAIL_VERIFICATION_PHRASES = [
  'email is not verified',
  'email has not been verified',
  'email_not_verified',
  'verify your email',
  'confirm your email',
  'email verification',
  'email address is not verified',
]

export type Auth0ErrorLike = {
  error?: string | null
  error_description?: string | null
  message?: string | null
}

/**
 * True when Auth0 blocked login/signup because the account email is not verified yet.
 * Matches common Universal Login callback query params and Auth0 SDK error objects.
 */
export function isAuth0EmailVerificationRequiredError(parts: {
  error?: string | null
  errorDescription?: string | null
  message?: string | null
}): boolean {
  const text = [parts.errorDescription, parts.message, parts.error]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  if (!text) return false

  if (EMAIL_VERIFICATION_PHRASES.some((phrase) => text.includes(phrase))) {
    return true
  }

  const mentionsEmail = text.includes('email') || text.includes('e-mail')
  const mentionsVerification =
    text.includes('verify') ||
    text.includes('verification') ||
    text.includes('verified') ||
    text.includes('confirm')

  return mentionsEmail && mentionsVerification
}

/** Read OAuth error query params from the Auth0 callback URL. */
export function readAuth0CallbackErrorFromSearch(search: string): {
  error: string | null
  errorDescription: string | null
} {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  return {
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  }
}

/** Read OAuth error params from query string or URL hash (Auth0 may use either). */
export function readAuth0CallbackErrorFromLocation(href?: string): {
  error: string | null
  errorDescription: string | null
} {
  const resolved =
    href ??
    (typeof window !== 'undefined' ? window.location.pathname + window.location.search + window.location.hash : '')
  if (!resolved && typeof window === 'undefined') {
    return { error: null, errorDescription: null }
  }

  const base = typeof window !== 'undefined' ? window.location.origin : 'https://example.com'
  const url = resolved.startsWith('http') ? new URL(resolved) : new URL(resolved, base)
  const fromSearch = readAuth0CallbackErrorFromSearch(url.search)
  if (fromSearch.error) return fromSearch

  const hash = url.hash.replace(/^#/, '')
  if (!hash) return { error: null, errorDescription: null }

  const hashParams = new URLSearchParams(hash)
  return {
    error: hashParams.get('error'),
    errorDescription: hashParams.get('error_description'),
  }
}

export function auth0ErrorLikeFromUnknown(err: unknown): Auth0ErrorLike | null {
  if (!err || typeof err !== 'object') return null
  const o = err as Record<string, unknown>
  return {
    error: typeof o.error === 'string' ? o.error : null,
    error_description: typeof o.error_description === 'string' ? o.error_description : null,
    message: typeof o.message === 'string' ? o.message : null,
  }
}

export function isAuth0EmailVerificationRequiredFromUnknown(err: unknown): boolean {
  const like = auth0ErrorLikeFromUnknown(err)
  if (!like) return false
  return isAuth0EmailVerificationRequiredError({
    error: like.error,
    errorDescription: like.error_description,
    message: like.message,
  })
}

export function storeEmailVerificationRequiredNotice(): void {
  try {
    sessionStorage.setItem(EMAIL_VERIFICATION_REQUIRED_STORAGE_KEY, '1')
  } catch {
    // ignore private mode / quota
  }
}

/** Returns true once per stored notice (consumes the flag). */
export function consumeEmailVerificationRequiredNotice(): boolean {
  try {
    if (sessionStorage.getItem(EMAIL_VERIFICATION_REQUIRED_STORAGE_KEY) !== '1') {
      return false
    }
    sessionStorage.removeItem(EMAIL_VERIFICATION_REQUIRED_STORAGE_KEY)
    return true
  } catch {
    return false
  }
}

/**
 * Detect email-verification block from callback URL and/or Auth0 SDK error; persist notice for sign-in UI.
 */
export function captureEmailVerificationRequiredFromCallback(
  auth0Error?: unknown,
  href?: string,
): boolean {
  const fromUrl = readAuth0CallbackErrorFromLocation(href)
  const blocked =
    isAuth0EmailVerificationRequiredError({
      error: fromUrl.error,
      errorDescription: fromUrl.errorDescription,
    }) || isAuth0EmailVerificationRequiredFromUnknown(auth0Error)

  if (blocked) {
    storeEmailVerificationRequiredNotice()
    return true
  }
  return false
}

/** True when the current URL is an Auth0 OAuth callback that failed (any error). */
export function hasAuth0CallbackErrorInLocation(href?: string): boolean {
  const { error } = readAuth0CallbackErrorFromLocation(href)
  return Boolean(error)
}
